import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import BookingsManager from '../components/admin/BookingsManager';
import OrdersManager from '../components/admin/OrdersManager';
import ServicesManager from '../components/admin/ServicesManager';
import VehiclesCategoryManager from '../components/admin/VehiclesCategoryManager';
import GaragesManager from '../components/admin/GaragesManager';
import TestimonialsManager from '../components/admin/TestimonialsManager';
import PricingManager from '../components/admin/PricingManager';
import SettingsManager from '../components/admin/SettingsManager';
import EmailTemplatesManager from '../components/admin/EmailTemplatesManager';
import ChatConversationsManager from '../components/admin/ChatConversationsManager';
import AnalyticsManager from '../components/admin/AnalyticsManager';
import DealersManager from '../components/admin/DealersManager';
import UsedCarCustomersManager from '../components/admin/UsedCarCustomersManager';
import ReelMusicManager from '../components/admin/ReelMusicManager';
import DealerPlansManager from '../components/admin/DealerPlansManager';
import DemoLeadsManager from '../components/admin/DemoLeadsManager';
import SiteImagesManager from '../components/admin/SiteImagesManager';
import { LayoutDashboard, ShoppingCart, Car, MessageSquare, Warehouse, Package, DollarSign, Settings, Mail, LogOut, MessageCircle, BarChart3, Building2, UserCheck, Music, Crown, Sparkles, Wrench, ImageIcon } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('bookings');
  const adminUsername = localStorage.getItem('admin_username') || 'Admin';

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (token) {
        await axios.post(`${BACKEND_URL}/api/auth/logout`, null, { params: { token } });
      }
      
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_username');
      
      toast({
        title: 'Logged Out',
        description: 'You have been logged out successfully'
      });
      
      navigate('/admin-login');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_username');
      navigate('/admin-login');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-8 shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <LayoutDashboard className="w-10 h-10" />
              <div>
                <h1 className="text-4xl font-bold">Admin Dashboard</h1>
                <p className="text-blue-100 mt-1">Manage your Propitstop platform</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-blue-100">Logged in as</p>
                <p className="font-semibold">{adminUsername}</p>
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="bg-white text-blue-600 hover:bg-blue-50 border-white"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-3 lg:grid-cols-6 xl:grid-cols-7 mb-8 h-auto bg-white shadow-lg rounded-lg p-2">
            <TabsTrigger 
              value="analytics" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <BarChart3 className="w-4 h-4" />
              Analytics
            </TabsTrigger>
            <TabsTrigger 
              value="bookings" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <ShoppingCart className="w-4 h-4" />
              Bookings
            </TabsTrigger>
            <TabsTrigger 
              value="orders" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <Package className="w-4 h-4" />
              Orders
            </TabsTrigger>
            <TabsTrigger 
              value="services" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <LayoutDashboard className="w-4 h-4" />
              Services
            </TabsTrigger>
            <TabsTrigger 
              value="garages" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <Warehouse className="w-4 h-4" />
              Garages
            </TabsTrigger>
            <TabsTrigger 
              value="vehicles" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <Car className="w-4 h-4" />
              Vehicles
            </TabsTrigger>
            <TabsTrigger 
              value="testimonials" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <MessageSquare className="w-4 h-4" />
              Testimonials
            </TabsTrigger>
            <TabsTrigger 
              value="pricing" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <DollarSign className="w-4 h-4" />
              Pricing
            </TabsTrigger>
            <TabsTrigger 
              value="settings" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Settings
            </TabsTrigger>
            <TabsTrigger 
              value="emails" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <Mail className="w-4 h-4" />
              Emails
            </TabsTrigger>
            <TabsTrigger 
              value="chatbot" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              AI Chat
            </TabsTrigger>
            <TabsTrigger
              value="dealers"
              data-testid="admin-tab-dealers"
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <Building2 className="w-4 h-4" />
              Dealers
            </TabsTrigger>
            <TabsTrigger
              value="used-car-customers"
              data-testid="admin-tab-used-car-customers"
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <UserCheck className="w-4 h-4" />
              Used Car Customers
            </TabsTrigger>
            <TabsTrigger
              value="reel-music"
              data-testid="admin-tab-reel-music"
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <Music className="w-4 h-4" />
              Reel Music
            </TabsTrigger>
            <TabsTrigger
              value="dealer-plans"
              data-testid="admin-tab-dealer-plans"
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <Crown className="w-4 h-4" />
              Plans
            </TabsTrigger>
            <TabsTrigger
              value="drive-leads"
              data-testid="admin-tab-drive-leads"
              className="data-[state=active]:bg-orange-500 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Drive Leads
            </TabsTrigger>
            <TabsTrigger
              value="pitcrew-leads"
              data-testid="admin-tab-pitcrew-leads"
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <Wrench className="w-4 h-4" />
              Pitcrew Leads
            </TabsTrigger>
            <TabsTrigger
              value="franchise-leads"
              data-testid="admin-tab-franchise-leads"
              className="data-[state=active]:bg-red-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Franchise Leads
            </TabsTrigger>
            <TabsTrigger
              value="site-images"
              data-testid="admin-tab-site-images"
              className="data-[state=active]:bg-purple-600 data-[state=active]:text-white py-4 rounded-lg transition-all font-semibold flex items-center gap-2"
            >
              <ImageIcon className="w-4 h-4" />
              Site Images
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analytics">
            <AnalyticsManager />
          </TabsContent>

          <TabsContent value="bookings">
            <BookingsManager />
          </TabsContent>

          <TabsContent value="orders">
            <OrdersManager />
          </TabsContent>

          <TabsContent value="services">
            <ServicesManager />
          </TabsContent>

          <TabsContent value="garages">
            <GaragesManager />
          </TabsContent>

          <TabsContent value="vehicles">
            <VehiclesCategoryManager />
          </TabsContent>

          <TabsContent value="testimonials">
            <TestimonialsManager />
          </TabsContent>

          <TabsContent value="pricing">
            <PricingManager />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsManager />
          </TabsContent>

          <TabsContent value="emails">
            <EmailTemplatesManager />
          </TabsContent>

          <TabsContent value="chatbot">
            <ChatConversationsManager />
          </TabsContent>

          <TabsContent value="dealers">
            <DealersManager />
          </TabsContent>

          <TabsContent value="used-car-customers">
            <UsedCarCustomersManager />
          </TabsContent>

          <TabsContent value="reel-music">
            <ReelMusicManager />
          </TabsContent>

          <TabsContent value="dealer-plans">
            <DealerPlansManager />
          </TabsContent>

          <TabsContent value="drive-leads">
            <DemoLeadsManager source="drive" title="Propitstop Drive — Dealer Demo Requests" brand="orange" />
          </TabsContent>

          <TabsContent value="pitcrew-leads">
            <DemoLeadsManager source="pitcrew" title="Pitcrew AI — Garage Demo Requests" brand="blue" />
          </TabsContent>

          <TabsContent value="franchise-leads">
            <DemoLeadsManager source="franchise" title="Franchise — Enquiries" brand="orange" />
          </TabsContent>

          <TabsContent value="site-images">
            <SiteImagesManager />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
